// native
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, shareReplay, startWith, tap } from 'rxjs/operators';

// addon
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { DateAdapter } from '@angular/material/core';

// service
import { AuthService } from './core/services/auth.service';
import { DemoService } from './core/services/demo.service';
import { TechallSessionService } from './core/services/techall-session.service';
import { LanguageService } from './core/services/language.service';
import { NavigationService } from './core/services/navigation.service';
import { DialogService } from './core/services/dialog.service';
import { PreferencesService } from './core/services/preferences.service';
import { PatientsJobService } from './core/services/patients-encrypt-job.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {

  protected onlineSubject = new Subject<boolean>();

  navExpanded = false;
  headerShown = true;
  headerTransparentMode = false;

  footerShown = true;

  navigationHiddenSubscription: Subscription;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public authService: AuthService,
    private demoService: DemoService,
    private toastService: ToastrService,
    public techallService: TechallSessionService,
    private translateService: TranslateService,
    private languageService: LanguageService,
    private navigationService: NavigationService,
    private dateAdapter: DateAdapter<any>,
    private dialogService: DialogService,
    private preferencesService: PreferencesService,
    private patientsJobService: PatientsJobService
  ) { }

  ngOnInit() {
    this.initializeRouteSubscription();
    this.navigationHiddenSubscription = this.navigationService.navigationHidden$.subscribe(hidden => {
      this.headerShown = !hidden;
      this.navExpanded = !hidden;
    });

    this.setOfflineNotification();

    this.translateService.use(this.languageService.appLanguage);
    // The default language must stay English (set in TranslateModule.forRoot):
    // it is the fallback for keys missing from a locale file. Setting it to the
    // selected language would render untranslated keys as raw key names.
    this.dateAdapter.setLocale(this.languageService.appLocale);

    if (this.authService.isLoggedIn)
      this.preferencesService.get().subscribe(() => {
        this.demoService.checkAndRedirect();
      });

    this.checkPatientEncryptionStatus();

    if (this.authService.isTechallAdmin)
      this.checkForCurrentTechallSession();
  }

  ngOnDestroy(): void {
    this.navigationHiddenSubscription?.unsubscribe();
  }

  setOfflineNotification() {
    window.addEventListener('online', () => this.onlineSubject.next(navigator.onLine));
    window.addEventListener('offline', () => this.onlineSubject.next(navigator.onLine));

    this.onlineSubject.pipe(
      distinctUntilChanged(),
      startWith(navigator.onLine),
      shareReplay({ refCount: true, bufferSize: 1 })
    ).subscribe(online => {
      if (!online) {
        this.toastService.error(this.translateService.instant('internetAccessRequired'));
        this.techallService.endCall();
      }
    });
  }

  isVideoHidden() {
    if (this.techallService.inCall || this.techallService.isWaiting)
      return false;
    if (this.authService.isTechallUser && this.techallService.currentSession && !this.techallService.currentSession.device) {
      return false;
    }
    return true;
  }

  initializeRouteSubscription() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.navigationService.getChildRoute(this.route)),
      filter((route: ActivatedRoute) => route.outlet === 'primary'),
      tap((route: ActivatedRoute) => {
        this.navigationService.isParameterizedRoute = Object.keys(route.snapshot?.params)?.length !== 0;
      }),
      mergeMap((route: ActivatedRoute) => route.data)
    ).subscribe(routeData => {
      this.headerShown = !!routeData?.hasNavigation;
      this.headerTransparentMode = this.router.url.includes('techall-mode') || this.router.url.includes('demo-mode');
      this.navExpanded = false;

      this.footerShown = !!routeData?.hasFooter;

      this.dialogService.visible$.next(false);
    });
  }

  checkPatientEncryptionStatus() {
    // if user is OGA continue the "encrypt existing patients" job if needed
    // the job will automatically store patients to local db
    if (this.preferencesService.isZeroPiiEnabled && this.authService.isOfficeGroupAdmin)
      this.patientsJobService.isEncryptionJobNeeded().subscribe(isNeeded => {
        if (isNeeded)
          this.patientsJobService.encryptExistingPatients(true);
      });
  }

  checkForCurrentTechallSession() {
    this.techallService.getSessions().subscribe(
      sessions => {
        this.techallService.currentSession = sessions?.results.find(session => !!session.admin);
      });
  }
}
